/**
 * SMS-side wiring for `@aegix/auth-*` (ADR-118).
 *
 * This module is the integration seam between SMS-app conventions
 * (legacy localStorage keys consumed by the shared `@tiaki/http-client`
 * package) and the canonical auth library that AIM, AIR, and ONE will
 * also consume. Three responsibilities:
 *
 *   1. **`smsStorageAdapter`** — a custom `StorageAdapter` that
 *      maps auth-core's versioned keys (`aegix.auth.v1.*`) to the
 *      legacy SMS keys (`aegix_sms_*`). The shared `@tiaki/http-client`
 *      package reads those legacy keys directly for the JWT,
 *      vendor-org masquerade, and refresh token — until that package
 *      also migrates to auth-core's storage layer, SMS keeps writing
 *      to the legacy locations so a single source of truth survives.
 *
 *   2. **`smsLoginAdapter` / `smsRefreshAdapter`** — pre-configured
 *      adapter instances pointing at the SMS-relative backend
 *      (empty `baseUrl` = same-origin SPA).
 *
 *   3. **`SMSUser`** — the SimpleJWT-shaped user the SMS app threads
 *      through the `<AuthProvider<SMSUser>>` generic. Keeps the
 *      shape that matched the prior in-app `User` interface, so the
 *      compat `useAuth()` shim (`hooks/useAuth.ts`) can satisfy
 *      consumer expectations.
 */

import {
  StorageKeys,
  createBridgedStorageAdapter,
  type StorageAdapter,
} from '@aegix/auth-core';
import {
  createSimpleJwtAdapter,
  createSimpleJwtRefreshAdapter,
  type SimpleJwtUser,
} from '@aegix/auth-identity';
import type { WithPermissions } from '@aegix/auth-permissions';
import type {
  Organization,
  WithVendorFlag,
} from '@aegix/auth-organization';
import { deviceIdFetch } from './deviceIdHeader';

/**
 * The SMS user shape. Extends the SimpleJwt baseline + the marker
 * interfaces from `auth-permissions` and `auth-organization` so the
 * downstream hooks (`useHasPermission`, `useIsVendorMasqueradeAvailable`)
 * type-check at the consume site.
 *
 * Keep this in sync with the SMS-side serializer fields. `SMSUser` is
 * the single user shape every SMS consumer imports (`id`, `email`,
 * `first_name`, `last_name`, `is_vendor_user`).
 */
export interface SMSUser extends SimpleJwtUser, WithVendorFlag {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  // Narrow `SimpleJwtUser.permissions?: string[]` to required + mutable.
  // `string[]` is structurally assignable to `WithPermissions`'s
  // `readonly string[]`, so the auth-permissions hooks
  // (`usePermissions`, `useHasPermission`) — which internally type the
  // user as `BaseUser & WithPermissions` — accept this shape without
  // SMSUser needing to `extends WithPermissions` explicitly. The
  // explicit extend would conflict with `SimpleJwtUser`'s optional
  // `permissions` field.
  permissions: string[];
  is_vendor_user?: boolean;
  // Duty-status fields. The backend accounts `UserSerializer`
  // (`apps/aegix-aim-backend/server/apps/accounts/serializers.py`)
  // UNCONDITIONALLY adds both to every login/`/me/` payload for all
  // clients. Mirrors AIM's `AIMUser.user_duty_id` — the SimpleJWT login
  // adapter spreads all non-jwt response fields into the user, so
  // declaring them here threads them through `login`/`fetchMe` automatically.
  //   - `user_duty_id` — the user's `user_duty` row pk (server-set when a
  //     `user_duty` record exists); addresses the correct duty row
  //     in the profile duty toggle without a separate fetch.
  //   - `is_on_duty` — the user's current duty state, seeded as the
  //     toggle's initial value before the duty query resolves.
  user_duty_id?: number | null;
  is_on_duty?: boolean | null;
  language?: string | null;
  /** Home organization ID (BaseUser.district FK), identifying org membership. */
  district?: number | null;
}

// Compile-time check: every consumer treats `SMSUser` as `WithPermissions`-
// compatible. If a future edit makes `permissions` optional or removes it,
// this line errors loudly instead of silently breaking `usePermissions`.
type _AssertSMSUserHasPermissions = SMSUser extends WithPermissions
  ? true
  : never;
const _checkSMSUserHasPermissions: _AssertSMSUserHasPermissions = true;
void _checkSMSUserHasPermissions;

/**
 * SMS-extended organization shape. The lib's `Organization` only
 * requires `id` + `name`; the SMS app additionally threads
 * `public_id` and `status` through every render path (deep links,
 * vendor masquerade dropdown, onboard-status gating).
 *
 * Consumers that need the extended fields cast the lib-returned
 * value to `ActiveOrganization` at the use site — the lib persists
 * unknown fields verbatim through `JSON.stringify`/`JSON.parse`, so
 * the round trip is lossless.
 */
export interface ActiveOrganization extends Organization {
  id: number;
  name: string;
  public_id: string;
  status: string;
}

/**
 * SMS's **pre-canonical** physical key names, kept only
 * as legacy slots `createBridgedStorageAdapter` reads-and-promotes
 * on first access. Writes now land on the canonical `aegix.auth.v1.*`
 * namespace; these `aegix_sms_*` entries get one read each before
 * they're deleted.
 *
 * Pre-canonical SMS kept all of these on `localStorage`. Post-
 * Phase-2 the canonical access token slot moves to `sessionStorage`
 * (uniform mobile pattern with AIM); the refresh token + user +
 * permissions + active-org stay on `localStorage`.
 *
 * Exported as `SMS_LEGACY_STORAGE_KEYS` for `lib/http-client.ts`'s
 * stale-masquerade clear, which must drain the legacy active-org
 * alongside the canonical one (otherwise the next read re-promotes
 * the stale value via the adapter's `legacyKeys` capability).
 */
const LEGACY_KEYS = {
  token: 'aegix_sms_token',
  refreshToken: 'aegix_sms_refresh_token',
  user: 'aegix_sms_user',
  permissions: 'aegix_sms_user_permission',
  activeOrganization: 'aegix_sms_active_org',
} as const;

/**
 * Declare SMS's pre-canonical physical names per logical
 * lib key. The adapter walks this list on first read when canonical
 * is empty, promotes the value to canonical, and deletes the legacy
 * entry. Sessions written by the pre-canonical code path migrate
 * forward on first access — the "no-squash" gate.
 *
 * Returns frozen module-scope tuples so the per-read call is a
 * lookup, not an allocation.
 */
const TOKEN_LEGACY: readonly string[] = Object.freeze([LEGACY_KEYS.token]);
const REFRESH_LEGACY: readonly string[] = Object.freeze([
  LEGACY_KEYS.refreshToken,
]);
const USER_LEGACY: readonly string[] = Object.freeze([LEGACY_KEYS.user]);
const PERMISSIONS_LEGACY: readonly string[] = Object.freeze([
  LEGACY_KEYS.permissions,
]);
const ACTIVE_ORG_LEGACY: readonly string[] = Object.freeze([
  LEGACY_KEYS.activeOrganization,
]);
const NO_LEGACY: readonly string[] = Object.freeze([]);

function smsLegacyKeys(libKey: string): readonly string[] {
  if (libKey === StorageKeys.token) return TOKEN_LEGACY;
  if (libKey === StorageKeys.refreshToken) return REFRESH_LEGACY;
  if (libKey === StorageKeys.user) return USER_LEGACY;
  if (libKey === StorageKeys.permissions) return PERMISSIONS_LEGACY;
  if (libKey === StorageKeys.activeOrganization) return ACTIVE_ORG_LEGACY;
  return NO_LEGACY; // heartbeat + any future lib-internal key
}

/**
 * SMS adopts the **uniform mobile pattern**: access token
 * lives in `sessionStorage` (ephemeral, dropped on tab close);
 * refresh token + user + permissions + active-org stay in
 * `localStorage` (persistent across sessions). Matches AIM (and ONE
 * once its backend lands a refresh token) so behavior, surface, and
 * lint surface are identical across all three apps.
 *
 * The companion piece is Phase 1's boot-time refresh
 * (`libs/auth-identity` provider reconcile) — when a fresh load
 * finds access empty in sessionStorage but refresh present in
 * localStorage, the provider mints a new access via the refresh
 * adapter before deciding logged-out. Mobile-pattern's missing half.
 *
 * Receives the **physical** key (post-promotion this is canonical;
 * during promotion it may transiently be the legacy
 * `aegix_sms_token`). Pre-canonical SMS wrote the token to
 * `localStorage`, NOT `sessionStorage` — so the legacy spelling
 * stays on `localStorage` for the promotion read. The adapter then
 * writes the canonical (sessionStorage) and deletes the legacy
 * entry; the end state matches the mobile pattern within one read.
 */
function smsStorageBackend(physical: string): Storage | null {
  if (typeof window === 'undefined') return null;
  if (physical === StorageKeys.token) {
    return typeof window.sessionStorage !== 'undefined'
      ? window.sessionStorage
      : null;
  }
  // Everything else (including the legacy access token at
  // `aegix_sms_token`, which pre-canonical SMS kept on localStorage)
  // resolves to localStorage.
  return typeof window.localStorage !== 'undefined'
    ? window.localStorage
    : null;
}

/**
 * Create a storage adapter for SMS. Writes land on the
 * canonical `aegix.auth.v1.*` namespace, with the access token in
 * `sessionStorage` (mobile pattern); existing sessions written by
 * the pre-canonical code path (`aegix_sms_*` on localStorage)
 * migrate forward losslessly on first read via the adapter's
 * `legacyKeys` capability.
 *
 * Raw-token / JSON-rest encoding, the `__cookie__` scrub, the
 * JSON-quoted self-heal, defensive try/catch, and the memory fallback
 * all live in `createBridgedStorageAdapter`. SMS supplies:
 *   - `backendFor` for the access-token-in-sessionStorage split.
 *   - `legacyKeys` for the `aegix_sms_*` → canonical migration.
 */
export function createSmsStorageAdapter(): StorageAdapter {
  return createBridgedStorageAdapter({
    backendFor: smsStorageBackend,
    legacyKeys: smsLegacyKeys,
  });
}

/**
 * Process-wide SMS storage adapter: the ONE adapter both
 * `<AuthProvider storage=…>` (App.tsx) and `@tiaki/http-client`
 * (`lib/http-client.ts` via `httpStorageFromAdapter`) consume — the http
 * client never reads `localStorage` raw. Non-`<AuthProvider>`
 * consumers (WebSocket ticket-exchange, reunification service) read the
 * JWT via `getSmsAccessToken` rather than touching `localStorage`
 * directly — enforced by the `aegix/no-raw-token-storage-access` rule.
 */
export const smsStorage = createSmsStorageAdapter();

/** Read the current SMS access token (raw JWT string) or null. */
export function getSmsAccessToken(): string | null {
  return smsStorage.get<string>(StorageKeys.token);
}

/**
 * Read the current SMS refresh token (raw JWT string, localStorage)
 * or null. Parity with `getAimRefreshToken` so non-`<AuthProvider>`
 * consumers can introspect the persistent credential without
 * touching `localStorage` directly (which would trip
 * `aegix/no-raw-token-storage-access`).
 */
export function getSmsRefreshToken(): string | null {
  return smsStorage.get<string>(StorageKeys.refreshToken);
}

/**
 * Pre-configured SimpleJWT login adapter for SMS. SMS is served
 * same-origin as the backend so `baseUrl` stays empty (relative URLs).
 *
 * The adapter's raw-fetch surfaces are SMS's credential mints —
 * `/rest/sessions` (password login, the request the backend mints the
 * signed `device_id` claim from), `bootstrapFromSso` (cookie-source
 * mint), and `/api/v2/auth/logout/` (whose FCM deactivation matches on
 * the device header). `deviceIdFetch` stamps the persisted device id on
 * all of them; a browser with nothing persisted sends no header, which the
 * backend reads as a legacy, unscoped session.
 */
export const smsLoginAdapter = createSimpleJwtAdapter<SMSUser>({
  fetchImpl: deviceIdFetch,
});

/**
 * Pre-configured refresh adapter — when the access token rotates
 * via the `x-refreshed-token` header (handled inside
 * `@tiaki/http-client`), the SMS app does NOT need to use this
 * adapter directly. It's wired into `<AuthProvider>` for the
 * `useAuthActions().refresh()` escape hatch only.
 */
export const smsRefreshAdapter = createSimpleJwtRefreshAdapter();

export { LEGACY_KEYS as SMS_LEGACY_STORAGE_KEYS };
